/**
 * The signed-in customer's company verification (KYC) state, shared by the
 * header prompt, the Account navigation and the Verification page.
 *
 * One GET /kyc per page load, however many components ask: the first caller
 * starts the request and everyone else awaits the same promise. The
 * Verification page replaces the cached view with the one each upload or
 * submit returns, so the header and the navigation update without a refetch.
 *
 * The token does not carry account_type or kyc_status, so this endpoint is
 * the only source for either. It is readable by every user of the company;
 * uploads and document links are admin-only and enforced by the server.
 */
import { computed, ref } from "vue";
import axios from "axios";

export type KycStatus =
  | "not_required"
  | "pending"
  | "under_review"
  | "approved"
  | "rejected"
  | "exempt";

export type ReviewStatus = "pending" | "approved" | "rejected";

export type Journey =
  | "first_time"
  | "requested"
  | "replace"
  | "expired"
  | "in_review"
  | "verified"
  | "exempt"
  | "not_applicable";

/** Journeys where the customer has documents to send. */
export const ACTION_JOURNEYS: Journey[] = ["first_time", "requested", "replace", "expired"];

export interface KycDocument {
  document_id: number;
  doc_type: string;
  /** The number on the document, when its type asks for one. */
  document_number: string | null;
  /** YYYY-MM-DD, when its type asks for an expiry. */
  expires_at: string | null;
  expired: boolean;
  /** Expires within 30 days. */
  expiring_soon: boolean;
  /** Uploaded by Xobo on the company's behalf. */
  uploaded_by_staff: boolean;
  original_filename: string | null;
  mime_type: string;
  bytes: number;
  review_status: ReviewStatus;
  rejection_reason_code: string | null;
  rejection_reason_label: string | null;
  rejection_note: string | null;
  reviewed_at: string | null;
  uploaded_at: string;
  uploaded_by_name: string | null;
}

export interface KycDocumentType {
  doc_type: string;
  label: string;
  is_required: boolean;
  requires_number: boolean;
  requires_expiry: boolean;
  document: KycDocument | null;
}

export interface KycView {
  company: {
    company_id: number;
    company_name: string;
    account_type: "business" | "retail" | null;
    registration_number: string | null;
    kra_pin: string | null;
    kyc_status: KycStatus | null;
    kyc_submitted_at: string | null;
    kyc_reviewed_at: string | null;
    kyc_rejection_note: string | null;
    kyc_requested_at: string | null;
    date_signed_up: string | null;
  };
  /** Which situation the customer is in; drives the guided flow. */
  journey: Journey;
  can_upload: boolean;
  /** Whether verification before ordering is switched on, and whether it stops this company now. */
  order_gate?: { enabled: boolean; blocks_orders: boolean };
  progress: { required: number; uploaded: number; approved: number; rejected: number };
  document_types: KycDocumentType[];
}

const view = ref<KycView | null>(null);
const error = ref("");
let inflight: Promise<KycView | null> | null = null;
// The token the cache belongs to. Logging out and in as someone else does not
// reload the page, so a cache from the previous session must not survive it.
let cachedFor = "";

export const kycUrl = (path = "") =>
  `${import.meta.env.VITE_ONBOARDING_SERVICE}kyc${path}`;

export const authHeaders = () => {
  const token = localStorage.getItem("_panda");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** The server's own explanation when it gives one, else a generic line. */
export const reasonFrom = (err: any, fallback: string) =>
  err?.response?.data?.reason || err?.response?.data?.message || fallback;

async function fetchKyc(token: string): Promise<KycView | null> {
  try {
    const res = await axios.get(kycUrl(), { headers: { Authorization: `Bearer ${token}` } });
    // A response for a session that has since ended is dropped.
    if (token !== cachedFor) return null;
    view.value = res.data?.data || null;
    error.value = "";
  } catch (err) {
    if (token !== cachedFor) return null;
    error.value = reasonFrom(err, "Could not load your verification status.");
  }
  return view.value;
}

/**
 * Upload one document for the signed-in company. Resolves to the server's
 * fresh view (null when it could not read it back) and whether this
 * completed the set and sent the company for review; rejects with the
 * server's own reason.
 */
export async function uploadKycDocument(args: {
  docType: string;
  file: File;
  documentNumber?: string;
  expiresAt?: string;
  onProgress?: (percent: number) => void;
}): Promise<{ view: KycView | null; submitted: boolean }> {
  const form = new FormData();
  form.append("doc_type", args.docType);
  if (args.documentNumber) form.append("document_number", args.documentNumber);
  if (args.expiresAt) form.append("expires_at", args.expiresAt);
  form.append("file", args.file);
  const res = await axios.post(kycUrl("/documents"), form, {
    headers: authHeaders(),
    onUploadProgress: (p) => {
      if (p.total && args.onProgress) args.onProgress(Math.round((p.loaded / p.total) * 100));
    },
  });
  return { view: res.data?.data || null, submitted: !!res.data?.submitted };
}

/**
 * May this company upload this document type now? Mirrors the server
 * (companyKycModel.assertUploadAllowed) so a button is only offered when
 * pressing it can work.
 *   Optional types: at any time while in verification or verified, including
 *   replacing one already sent; it waits as "pending verification".
 *   Required types:
 *     pending       any
 *     under_review  only one that is still missing
 *     rejected      anything not already accepted
 *     otherwise     nothing
 */
export function canUploadType(view: KycView | null, t: KycDocumentType): boolean {
  if (!view?.can_upload) return false;
  const status = view.company.kyc_status;
  if (!t.is_required) return ["pending", "under_review", "rejected", "approved"].includes(status || "");
  if (status === "under_review") return !t.document;
  if (status === "rejected") return t.document?.review_status !== "approved";
  return status === "pending";
}

/**
 * The customer's word for a document Xobo has not decided on yet: "Uploaded"
 * while it waits for the company to send its documents (still pending, or a
 * required replacement while other rejected ones remain), "Pending
 * verification" once Xobo can check it (in review, an optional document, or
 * anything added after verification).
 */
export const awaitingLabel = (status: KycStatus | null | undefined, isRequired: boolean) =>
  status === "pending" || (status === "rejected" && isRequired) ? "Uploaded" : "Pending verification";

/** Documents sent but not yet decided by Xobo, outside a company review. */
export const docsPendingVerification = (view: KycView | null) =>
  !view || view.company.kyc_status === "under_review"
    ? []
    : view.document_types.filter(
        (t) => !t.is_required && t.document?.review_status === "pending" && !t.document.expired,
      );

export function useCompanyKyc() {
  /** Load once per page load; pass force to refetch. */
  const load = (force = false) => {
    const token = localStorage.getItem("_panda") || "";
    if (token !== cachedFor) {
      view.value = null;
      error.value = "";
      inflight = null;
      cachedFor = token;
    }
    if (!token) return Promise.resolve(null);
    if (!inflight || force) inflight = fetchKyc(token);
    return inflight;
  };

  /** Adopt the view an upload or submit returned. null means re-fetch. */
  const adopt = (next: KycView | null) => {
    if (next) view.value = next;
    else void load(true);
  };

  const isBusiness = computed(() => view.value?.company.account_type === "business");
  const status = computed(() => view.value?.company.kyc_status || null);
  /** The customer has something to do: upload, or replace a rejected file. */
  const needsAction = computed(
    () => isBusiness.value && (status.value === "pending" || status.value === "rejected"),
  );

  return { view, error, load, adopt, isBusiness, status, needsAction };
}

export type Tone = "muted" | "info" | "warn" | "ok" | "bad";

/**
 * Short status flag for the navigation, the header prompt and the business
 * profile. null where there is nothing worth flagging: personal accounts, and
 * companies exempt because they predate verification.
 */
export function kycStatusFlag(
  status: KycStatus | null,
): { short: string; label: string; tone: Tone } | null {
  switch (status) {
    case "pending":
      return { short: "To do", label: "Verify your company", tone: "warn" };
    case "rejected":
      return { short: "Action needed", label: "Documents need replacing", tone: "bad" };
    case "under_review":
      return { short: "In review", label: "Verification in review", tone: "info" };
    case "approved":
      return { short: "Verified", label: "Verified company", tone: "ok" };
    default:
      return null;
  }
}
